/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useEffect, useState } from 'react'
import { Box, Button, IconButton, Select, styled } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { CircleErrorIcon, CloseIcon } from '@rsmus/myrsm-icons'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import {
  PbiReportDefinitionViewModel,
  PbiChildReportViewModel,
} from '@rsmus/ecp-userservice'
import api from '../../api'
import { Styles } from '../../types'
import ResponsiveModal from '../../rsmCoreComponents/components/ResponsiveModal'
import TextField from '../TextField/TextField'
import { tokens } from '../../styles/materialTheme'

type Mode = 'create' | 'edit'

type ExtendedReportDefinitionViewModel = PbiReportDefinitionViewModel & {
  effectiveIdentityEnabled?: boolean
}

interface ChildReportModalProps {
  open: boolean
  onClose: () => void
  onSave?: (childReport: PbiChildReportViewModel) => void
  mode?: Mode
  initialData?: PbiChildReportViewModel
  onUpdate?: (childReport: PbiChildReportViewModel) => void
  onRemove?: (childReportId: number) => void
  reportDefinitions?: ExtendedReportDefinitionViewModel[]
}

type FormValues = {
  pbiReportDefinitionId: string
  childReportDisplayName: string
  childReportName: string
  pbiWorkspaceId: string
  pbiObjectId: string
  isDashboard: string
}

const styles: Styles = {
  container: (theme) => ({
    padding: '1.5rem',
    [theme.breakpoints.down('tablet')]: {
      paddingBottom: '1.5rem',
    },
  }),
  fieldContainer: (theme) => ({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '1.5rem',
    [theme.breakpoints.only('mobile')]: {
      gridTemplateColumns: '1fr',
    },
  }),
  title: (theme) => ({
    fontFamily: 'Prelo-Book, sans-serif',
    fontSize: '2.25rem',
    lineHeight: '2.5rem',
    paddingBottom: '1.75rem',
    border: '0',
    [theme.breakpoints.only('mobile')]: {
      fontSize: '2rem',
      paddingTop: '1rem',
    },
  }),
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexFlow: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: '1.5rem',
    gap: '0.625rem',
    border: '0',
  },
  errorHeader: (theme) => ({
    fontFamily: tokens.type.fontFamilies.preloBold,
    fontSize: '1.125rem',
    lineHeight: '1.75rem',
    [theme.breakpoints.only('mobile')]: {
      fontSize: '1.5rem',
      paddingTop: '1rem',
    },
  }),
  errorMessage: (theme) => ({
    fontFamily: tokens.type.fontFamilies.preloBook,
    fontSize: '1.125rem',
    lineHeight: '1.5rem',
    [theme.breakpoints.only('mobile')]: {
      fontSize: '1rem',
      paddingBottom: '1rem',
    },
  }),
  errorContainer: {
    width: '100%',
    height: '7.8125rem',
    padding: '1.5rem',
    backgroundColor: tokens.colors.warning.secondary,
    marginBottom: '1.5rem',
  },
  infoText: {
    fontFamily: 'Prelo-Book, sans-serif',
    fontSize: '.875rem',
    lineHeight: '1.25rem',
    color: tokens.colors.rsmGray.copy,
    paddingBottom: '1rem',
  },
}

const StyledSelectField = styled(Select)({
  width: '100%',
  '.MuiOutlinedInput-root': {
    width: '100%',
    maxWidth: '100%',
  },
  '.MuiSelect-select': {
    fontFamily: 'Prelo-Book',
  },
})

const StyledLabel = styled('label')(({ theme }) => ({
  fontFamily: 'Prelo-Black, sans-serif',
  fontSize: '1rem',
  paddingBottom: '0.5rem',
  color: theme.palette.text.primary,
  display: 'block',
}))

const ChildReportModal = ({
  open,
  onClose,
  onSave,
  mode = 'create',
  initialData,
  onUpdate,
  onRemove,
  reportDefinitions = [],
}: ChildReportModalProps) => {
  const { t } = useTranslation()

  const methods = useForm<FormValues>({
    defaultValues: {
      pbiReportDefinitionId: '',
      childReportDisplayName: '',
      childReportName: '',
      pbiWorkspaceId: '',
      pbiObjectId: '',
      isDashboard: 'false',
    },
  })

  const [hasError, setHasError] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const eligibleParentReports = reportDefinitions.filter(
    (report) => report.effectiveIdentityEnabled === true,
  )

  const guidRegex =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/
  const childReportNameRegex = /^[a-zA-Z0-9_-]+$/

  const formatGUID = (value: string) => value.toUpperCase().trim()

  useEffect(() => {
    const subscription = methods.watch(() => {
      if (hasError) {
        setHasError(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [methods, hasError])

  useEffect(() => {
    if (!open) return

    setHasError(false)
    setIsSaving(false)
    methods.clearErrors()

    if (mode !== 'edit' || !initialData) {
      methods.reset({
        pbiReportDefinitionId: '',
        childReportDisplayName: '',
        childReportName: '',
        pbiWorkspaceId: '',
        pbiObjectId: '',
        isDashboard: 'false',
      })
      return
    }

    methods.reset({
      pbiReportDefinitionId: String(initialData.pbiReportDefinitionId ?? ''),
      childReportDisplayName: initialData.childReportDisplayName ?? '',
      childReportName: initialData.childReportName ?? '',
      pbiWorkspaceId: initialData.pbiWorkspaceId ?? '',
      pbiObjectId: initialData.pbiObjectId ?? '',
      isDashboard: String(!!initialData.isDashboard),
    })
  }, [open, mode, initialData, methods])

  const handleOnClose = useCallback(() => {
    methods.reset()
    setHasError(false)
    onClose()
  }, [methods, onClose])

  const handleCloseIconKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        handleOnClose()
      }
    },
    [handleOnClose],
  )

  const guidValidation = (value: string) => {
    if (!guidRegex.test(value.trim().toUpperCase())) {
      return t('PbiChildReport.Validation.InvalidGuid')
    }
    return true
  }

  const childReportNameValidation = (value: string) => {
    if (!childReportNameRegex.test(value.trim())) {
      return t('PbiChildReport.Validation.ChildReportNameInvalid')
    }
    return true
  }

  const handleAddNewChildReport = async () => {
    setHasError(false)
    setIsSaving(true)

    const childReport: PbiChildReportViewModel = {
      id: 0,
      pbiReportDefinitionId: parseInt(
        methods.getValues('pbiReportDefinitionId'),
        10,
      ),
      childReportDisplayName: methods
        .getValues('childReportDisplayName')
        .trim(),
      childReportName: methods.getValues('childReportName').trim(),
      pbiWorkspaceId: formatGUID(methods.getValues('pbiWorkspaceId')),
      pbiObjectId: formatGUID(methods.getValues('pbiObjectId')),
      isDashboard: methods.getValues('isDashboard') === 'true',
    }

    try {
      await api.user.report_AddChildReport(childReport)
      methods.reset()
      onSave?.(childReport)
    } catch (error: unknown) {
      const apiError = error as { response?: string }
      const errorMessage = apiError?.response ?? ''

      if (
        errorMessage.includes(
          'ChildReportName must be unique per parent report.',
        )
      ) {
        methods.setError('childReportName', {
          message: t('PbiChildReport.Validation.ChildReportNameUnique'),
        })
      } else if (
        errorMessage.includes('ChildReportDisplayName must be unique')
      ) {
        methods.setError('childReportDisplayName', {
          message: t('PowerBI.ReportModal.DisplayNameMustBeUnique'),
        })
      } else if (errorMessage.includes('ChildReportDisplayName is required')) {
        methods.setError('childReportDisplayName', {
          message: t('PbiChildReport.Validation.DisplayNameRequired'),
        })
      } else {
        setHasError(true)
      }
    } finally {
      setIsSaving(false)
    }
  }

  const handleUpdateChildReport = async () => {
    if (!initialData) return

    setHasError(false)
    setIsSaving(true)

    const childReport: PbiChildReportViewModel = {
      ...initialData,
      pbiReportDefinitionId: parseInt(
        methods.getValues('pbiReportDefinitionId'),
        10,
      ),
      childReportDisplayName: methods
        .getValues('childReportDisplayName')
        .trim(),
      childReportName: methods.getValues('childReportName').trim(),
      pbiWorkspaceId: formatGUID(methods.getValues('pbiWorkspaceId')),
      pbiObjectId: formatGUID(methods.getValues('pbiObjectId')),
      isDashboard: methods.getValues('isDashboard') === 'true',
    }

    try {
      await api.user.report_UpdateChildReport(childReport)
      methods.reset()
      onUpdate?.(childReport)
    } catch (error: unknown) {
      const apiError = error as { response?: { data?: { message?: string } } }
      const errorMessage = apiError?.response?.data?.message ?? ''

      if (errorMessage.includes('Child report name must be unique')) {
        methods.setError('childReportName', {
          message: t('PbiChildReport.Validation.ChildReportNameUnique'),
        })
      } else if (errorMessage.includes('Display name is required')) {
        methods.setError('childReportDisplayName', {
          message: t('PbiChildReport.Validation.DisplayNameRequired'),
        })
      } else {
        setHasError(true)
      }
    } finally {
      setIsSaving(false)
    }
  }

  const renderParentReportSelectField = () => (
    <Box>
      <StyledLabel htmlFor="pbiReportDefinitionId">
        {t('PbiChildReport.Columns.ParentReport')} *
        <span className="sr-only">{t('srOnlyRequired')}</span>
      </StyledLabel>
      <Controller
        name="pbiReportDefinitionId"
        control={methods.control}
        rules={{
          required: t('PbiChildReport.Validation.ParentReportRequired'),
        }}
        render={({
          field: { onChange, value, ref },
          fieldState: { error },
        }) => (
          <>
            <StyledSelectField
              native
              id="pbiReportDefinitionId"
              inputRef={ref}
              value={value}
              onChange={(event) => {
                onChange(event)
                if (mode === 'create') {
                  methods.setValue('childReportDisplayName', '')
                  methods.setValue('childReportName', '')
                  methods.setValue('pbiWorkspaceId', '')
                  methods.setValue('pbiObjectId', '')
                  methods.setValue('isDashboard', 'false')
                  methods.clearErrors([
                    'childReportDisplayName',
                    'childReportName',
                    'pbiWorkspaceId',
                    'pbiObjectId',
                    'isDashboard',
                  ])
                  setHasError(false)
                }
              }}
              error={!!error}
              disabled={mode === 'edit'}
              inputProps={{ 'aria-describedby': 'pbiReportDefinitionIdError' }}
              data-testid="Sel_ChildReportModal_ParentReport">
              <option value="">
                {t('PowerBI.ReportModal.Placeholder', 'Select')}
              </option>
              {eligibleParentReports.map((report) => (
                <option key={report.id} value={report.id?.toString()}>
                  {report.reportName}
                </option>
              ))}
            </StyledSelectField>
            {error && (
              <Box
                component="span"
                sx={{
                  display: 'block',
                  color: tokens.colors.rsmRed.secondary,
                  fontFamily: 'Prelo-Book, sans-serif',
                  fontSize: '.875rem',
                  paddingTop: '0.25rem',
                }}>
                {error.message}
              </Box>
            )}
          </>
        )}
      />
    </Box>
  )

  const renderIsDashboardSelectField = () => (
    <Box>
      <StyledLabel htmlFor="isDashboard">
        {t('PbiChildReport.Columns.IsDashboard')} *
        <span className="sr-only">{t('srOnlyRequired')}</span>
      </StyledLabel>
      <Controller
        name="isDashboard"
        control={methods.control}
        defaultValue="false"
        render={({ field: { onChange, value, ref } }) => (
          <StyledSelectField
            native
            id="isDashboard"
            inputRef={ref}
            value={value}
            onChange={onChange}
            inputProps={{ 'aria-describedby': 'isDashboardError' }}
            data-testid="Sel_ChildReportModal_IsDashboard">
            <option value="false">{t('PowerBI.ReportModal.No')}</option>
            <option value="true">{t('PowerBI.ReportModal.Yes')}</option>
          </StyledSelectField>
        )}
      />
    </Box>
  )

  const title =
    mode === 'create'
      ? t('PowerBI.ChildReportModal.Header')
      : t('PowerBI.ChildReportModal.EditHeader')

  const primaryLabel =
    mode === 'create'
      ? t('PowerBI.ChildReportModal.AddChildReport')
      : t('PowerBI.ChildReportModal.UpdateChildReport')

  const onSubmit =
    mode === 'create'
      ? () => handleAddNewChildReport()
      : () => handleUpdateChildReport()

  return (
    <Box className="Rsm-ProfileModal">
      <ResponsiveModal
        open={open}
        onClose={handleOnClose}
        aria-label={title}
        desktopMaxHeight="40rem"
        removeHeaderSpacing
        removeCloseButton
        removeContentSpacing
        data-testid="Lbl_ChildReport_AddNewDialog_Modal">
        <FormProvider {...methods}>
          <Box
            component="form"
            sx={styles.container}
            onSubmit={methods.handleSubmit(onSubmit)}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton
                sx={{ padding: 0 }}
                tabIndex={0}
                aria-label={t('ModalDialog.CloseDialog')}
                data-testid="Btn_Modal_Close"
                onClick={handleOnClose}
                onKeyDown={(event: React.KeyboardEvent) =>
                  handleCloseIconKeyDown(event)
                }
                disableFocusRipple
                disableRipple>
                <CloseIcon size={12.5} />
              </IconButton>
            </Box>

            <Box component="h1" sx={styles.title}>
              {title}
            </Box>

            <Box sx={styles.infoText}>
              {t('PowerBI.ChildReportModal.Info.EffectiveIdentityOnly')}
            </Box>

            {hasError && (
              <Box sx={styles.errorContainer}>
                <Box
                  sx={{
                    alignItems: 'center',
                    height: '100%',
                    display: 'flex',
                  }}>
                  <CircleErrorIcon
                    size="4rem"
                    color={tokens.colors.rsmGray.copy}
                    fillColor
                  />
                  <Box sx={{ paddingLeft: '1.25rem' }}>
                    <Box component="h2" sx={styles.errorHeader}>
                      {t('PbiChildReport.Errors.SaveErrorHeader')}
                    </Box>
                    <Box component="p" sx={styles.errorMessage}>
                      {t('PbiChildReport.Errors.SaveErrorMessage')}
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}

            <Box sx={styles.fieldContainer}>
              {renderParentReportSelectField()}

              <TextField
                labelName={t('PbiChildReport.Columns.DisplayName')}
                fieldName="childReportDisplayName"
                componentName="ChildReportModal"
                maxLength={200}
                fieldRequired
                placeholderText={t('PowerBI.ReportModal.Placeholder')}
              />

              <TextField
                labelName={t(
                  'PbiChildReport.Fields.ChildReportName',
                  'Child Report Name',
                )}
                fieldName="childReportName"
                componentName="ChildReportModal"
                maxLength={100}
                fieldRequired
                placeholderText={t('PowerBI.ReportModal.Placeholder')}
                fieldValidation={(value) => childReportNameValidation(value)}
              />

              <TextField
                labelName={t(
                  'PbiChildReport.Fields.PBIWorkspaceID',
                  'Power BI Workspace ID',
                )}
                fieldName="pbiWorkspaceId"
                componentName="ChildReportModal"
                fieldRequired
                placeholderText={t('PowerBI.ReportModal.Placeholder')}
                fieldValidation={(value) => guidValidation(value)}
              />

              <TextField
                labelName={t(
                  'PbiChildReport.Fields.PBIObjectID',
                  'Power BI Report/Object ID',
                )}
                fieldName="pbiObjectId"
                componentName="ChildReportModal"
                fieldRequired
                placeholderText={t('PowerBI.ReportModal.Placeholder')}
                fieldValidation={(value) => guidValidation(value)}
              />

              {renderIsDashboardSelectField()}
            </Box>

            <Box sx={styles.buttonContainer}>
              <Button
                variant="outlined"
                data-testid="Btn_ChildReportModal_Cancel"
                disabled={isSaving}
                onClick={handleOnClose}>
                {t('Common.Cancel', 'Cancel')}
              </Button>

              {mode === 'edit' && (
                <Button
                  variant="outlined"
                  disabled={isSaving}
                  onClick={() => initialData && onRemove?.(initialData.id)}
                  data-testid="Btn_EditChildReportDialog_Remove">
                  {t('PowerBI.ChildReportModal.RemoveChildReport')}
                </Button>
              )}

              <Button
                type="submit"
                variant="contained"
                disabled={isSaving}
                data-testid={
                  mode === 'create'
                    ? 'Btn_AddNewChildReportDialog_Add'
                    : 'Btn_EditChildReportDialog_Save'
                }>
                {primaryLabel}
              </Button>
            </Box>
          </Box>
        </FormProvider>
      </ResponsiveModal>
    </Box>
  )
}

export default ChildReportModal
